import {formatDate, formatDateTime} from './dateUtils';

/**
 * Reusable export formatters for consistent data formatting
 * Following DRY principle and clean architecture
 */

// Generic formatters
export const createSafeFormatter = <T>(
  extractor: (row: T) => unknown,
  fallback: string = 'غير محدد'
) => {
  return (_value: unknown, row: T): string => {
    const result = extractor(row);
    return result ? String(result) : fallback;
  };
};

export const createNestedFormatter = <T>(
  path: string[],
  fallback: string = 'غير محدد'
) => {
  return (_value: unknown, row: T): string => {
    let result: any = row;
    for (const key of path) {
      result = result?.[key];
      if (result === null || result === undefined) {
        return fallback;
      }
    }
    return String(result);
  };
};

// Date formatters
export const createDateFormatter = (fallback: string = 'غير محدد') => {
  return (value: unknown): string => {
    if (typeof value === 'string') {
      return formatDate(value);
    }
    return fallback;
  };
};

export const createDateTimeFormatter = (fallback: string = 'غير محدد') => {
  return (value: unknown): string => {
    if (typeof value === 'string') {
      return formatDateTime(value);
    }
    return fallback;
  };
};

// Status formatters
export const createStatusFormatter = (statusMap: Record<string, string>, fallback: string = 'غير محدد') => {
  return (value: unknown): string => {
    if (typeof value === 'string' && value in statusMap) {
      return statusMap[value];
    }
    return fallback;
  };
};

// Currency formatters
/**
 * Creates a currency formatter for display purposes
 * Includes currency text in the output (e.g., "1000 ريال")
 * @param currency - Currency text to append (default: 'ريال')
 * @returns Formatter function that adds currency text to numbers
 */
export const createCurrencyFormatter = (currency: string = 'ريال') => {
  return (value: unknown): string => {
    const numValue = typeof value === 'number' ? value : Number(value) || 0;
    return `${numValue} ${currency}`;
  };
};

/**
 * Creates a currency formatter for export purposes (Excel/CSV)
 * Returns raw numbers as strings without any formatting (no thousand separators, no currency text)
 * This ensures Excel treats them as numeric values for SUM and other functions
 * Example: 22000 -> "22000" (not "22,000"), 22000.50 -> "22000.5"
 * @returns Formatter function that returns raw numeric strings
 */
export const createCurrencyExportFormatter = () => {
  return (value: unknown): string => {
    if (value === null || value === undefined) {
      return '0';
    }

    const numValue = typeof value === 'number' ? value : Number(value);

    if (isNaN(numValue)) {
      return '0';
    }

    // Return raw number as string without any formatting
    // For whole numbers, return without decimals; for decimals, return as-is
    const isWholeNumber = numValue % 1 === 0;
    return isWholeNumber ? String(Math.round(numValue)) : String(numValue);
  };
};

// Array formatters
export const createArrayFormatter = (separator: string = ', ', fallback: string = 'لا يوجد') => {
  return (value: unknown): string => {
    if (Array.isArray(value) && value.length > 0) {
      return value.join(separator);
    }
    return fallback;
  };
};

// Boolean formatters
export const createBooleanFormatter = (trueText: string, falseText: string) => {
  return (value: unknown): string => {
    return value ? trueText : falseText;
  };
};

// Pre-configured formatters for common use cases
export const ExportFormatters = {
  // Date formatters
  date: createDateFormatter(),
  dateTime: createDateTimeFormatter(),
  dateWithFallback: (fallback: string) => createDateFormatter(fallback),
  dateTimeWithFallback: (fallback: string) => createDateTimeFormatter(fallback),

  // Status formatters
  visitStatus: createStatusFormatter({
    'in_progress': 'قيد التنفيذ',
    'completed': 'مكتملة',
    'cancelled': 'ملغية'
  }),

  paymentStatus: createStatusFormatter({
    'pending': 'في الانتظار',
    'approved': 'معتمد',
    'rejected': 'مرفوض'
  }),

  distributionStatus: createStatusFormatter({
    'PENDING': 'في الانتظار',
    'DELIVERED': 'تم التسليم',
    'PARTIALLY_DELIVERED': 'تسليم جزئي',
    'CANCELLED': 'ملغي',
    'RETURNED': 'مرتجع'
  }),

  serialStatus: createStatusFormatter({
    'available': 'متوفر',
    'assigned': 'مخصص',
    'sold': 'مباع'
  }),

  // Currency formatters (for display - includes currency text)
  currency: createCurrencyFormatter(),
  currencySAR: createCurrencyFormatter('ريال'),

  // Currency export formatters (for Excel - numbers only, no currency text)
  currencyExport: createCurrencyExportFormatter(),
  currencySARExport: createCurrencyExportFormatter(),

  // Array formatters
  tags: createArrayFormatter(', '),
  serials: createArrayFormatter(', ', 'لا يوجد'),

  // Boolean formatters
  activeStatus: createBooleanFormatter('نشط', 'غير نشط'),
  serialType: createBooleanFormatter('بسيريال', 'بدون سيريال'),

  // Nested object formatters
  storeName: createNestedFormatter(['store', 'storeName']),
  storeCity: createNestedFormatter(['store', 'city']),
  storeRegion: createNestedFormatter(['store', 'region']),
  visitorName: createNestedFormatter(['visitor', 'fullName']),
  visitorUsername: createNestedFormatter(['visitor', 'username']),
  marketerName: createNestedFormatter(['marketer', 'name']),
  storePhone: createNestedFormatter(['store', 'phone'])
};
